import { useState, useRef, useEffect } from "react";
import { TRACKS } from "../constants/tracks.constant";
import { PLATFORM_LINKS, DISCOGRAPHY_URL } from "../constants/links.constant";
import { trackPlayStarted, trackPlayed } from "../util/google-analytics.util";
import type { TrackPlayTrigger } from "../util/google-analytics.util";
import { TrackRow } from "./track-row.component";
import { SpotifyIcon } from "./icons/spotify.component";
import { AppleMusicIcon } from "./icons/apple-music.component";
import { AmazonMusicIcon } from "./icons/amazon-music.component";
import { YouTubeIcon } from "./icons/youtube.component";

const ICON_CLS = "text-primary/40 hover:text-primary transition-colors duration-300";

// YouTube counts a view at ~30s; for short tracks fall back to GA4's 50% video_progress milestone.
const PLAYED_THRESHOLD_SECONDS = 30;

export function Listen() {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const nextTriggerRef = useRef<TrackPlayTrigger>("manual");

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => {
      nextTriggerRef.current = "auto";
      setPlayingIndex((prev) => (prev === null ? null : (prev + 1) % TRACKS.length));
    };
    audio.addEventListener("ended", onEnded);
    return () => audio.removeEventListener("ended", onEnded);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playingIndex === null) {
      audio.pause();
      audio.currentTime = 0;
      return;
    }
    const track = TRACKS[playingIndex];
    if (!track) return;
    audio.src = `/tracks/${encodeURIComponent(track.fileName)}`;

    trackPlayStarted({
      title: track.title,
      index: playingIndex,
      trigger: nextTriggerRef.current,
    });
    nextTriggerRef.current = "manual";

    const threshold = Math.min(PLAYED_THRESHOLD_SECONDS, track.durationSeconds * 0.5);
    // Latch per play session — guarantees track_played fires at most once per play
    let playedFired = false;
    const onTimeUpdate = () => {
      setCurrentTime(Math.floor(audio.currentTime));
      if (!playedFired && audio.currentTime >= threshold) {
        playedFired = true;
        trackPlayed({ title: track.title, index: playingIndex });
      }
    };
    audio.addEventListener("timeupdate", onTimeUpdate);
    void audio.play().catch(() => setPlayingIndex(null));
    return () => audio.removeEventListener("timeupdate", onTimeUpdate);
  }, [playingIndex]);

  function handleTrackClick(index: number) {
    nextTriggerRef.current = "manual";
    setPlayingIndex((prev) => (prev === index ? null : index));
  }

  return (
    <section id="listen" className="pb-20 px-6 md:pb-35 md:px-13">
      <div className="max-w-6xl mx-auto border-t border-border pt-20 md:pt-35">
        <p className="section-label reveal">Listen</p>

        <div className="flex items-center gap-6 mb-12 reveal">
          <span className="text-xs tracking-widest uppercase text-muted whitespace-nowrap">
            Available on
          </span>
          <div className="flex items-center gap-5">
            <a href={PLATFORM_LINKS.spotify.href} target="_blank" rel="noreferrer">
              <SpotifyIcon size={22} className={ICON_CLS} />
            </a>
            <a href={PLATFORM_LINKS.appleMusic.href} target="_blank" rel="noreferrer">
              <AppleMusicIcon size={22} className={ICON_CLS} />
            </a>
            <a href={PLATFORM_LINKS.amazonMusic.href} target="_blank" rel="noreferrer">
              <AmazonMusicIcon size={22} className={ICON_CLS} />
            </a>
            <a href={PLATFORM_LINKS.youtube.href} target="_blank" rel="noreferrer">
              <YouTubeIcon size={22} className={ICON_CLS} />
            </a>
          </div>
        </div>

        <div className="border-t border-border-light reveal">
          {TRACKS.map((track, i) => (
            <TrackRow
              key={track.title}
              track={track}
              index={i}
              isPlaying={playingIndex === i}
              currentTime={playingIndex === i ? currentTime : undefined}
              onClick={() => handleTrackClick(i)}
            />
          ))}
        </div>

        <div className="mt-8 text-right reveal">
          <a
            href={DISCOGRAPHY_URL}
            className="text-[11px] tracking-[0.2em] uppercase text-muted no-underline border-b border-border pb-0.75 hover:text-primary transition-colors duration-300"
          >
            Full discography →
          </a>
        </div>
      </div>
      <audio ref={audioRef} preload="none" />
    </section>
  );
}
